// Angular Imports
import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Store} from '@ngrx/store';
import {ActivatedRoute} from '@angular/router';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {BehaviorSubject} from 'rxjs';
import { CSVTemplateGenerator } from '../../../features/tile-bulk-operations/creation/utils/csv-template-generator.util';
import { CSVConfigExporter } from '../../../features/tile-bulk-operations/shared/utils/csv-config-exporter.util';
import { DeviceType, DEVICE_TYPE_DISPLAY } from '../../../shared/models/device';
import { TileFormat } from '../../../shared/models/tile';
import { FormulaType, FORMULA_MAX_BINDINGS } from '../../../shared/models/formula-config';
import { FORMULA_TEMPLATES } from '../../../features/formula-tile/shared/constants/formula-templates.constants';
import { CsvFieldId } from '../../../features/tile-bulk-operations/shared/constants/csv-columns.constants';
import { TAG_PROFILES } from '../../../shared/constants/tag-profiles.constants';
import { BacnetPointType } from '../../../shared/models/point';
import { ModbusDataType } from '../../../shared/models/modbus';
import { LoRaWANPointType, LoRaWANPointFormat } from '../../../shared/models/lorawan';
import {
  DECIMAL_OPTIONS,
  CONVERSION_OPTIONS,
  MULTIPLIER_OPTIONS
} from '../../../shared/constants/tile-config.constants';
import { ProtocolPropertyLabel } from '../../../shared/constants/protocol-property-label.constants';
import { ColorOption } from '../../../shared/models/appearance';

// App Services
import {TitleChangedAction} from '../../../core/actions/title.actions';
import {AsideService} from '../../../core/services/aside.service';
import {CopyToastService} from '../../../shared/components/copy-toast/copy-toast.service';
import {FeatureFlagsService, FeatureFlag} from '../../../core/services/feature-flags.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';

// App Components
import {IconPickerComponent} from '../../components/icon-picker/icon-picker.component';
import {ColorPickerComponent} from '../../components/color-picker/color-picker.component'
import {SortTableComponent} from '../../components/sort-table/sort-table.component';
import {TileEditorComponent} from '../../components/tile-editor/tile-editor.component';
import {SubnavComponent, SubnavItem} from '../../components/subnav/subnav.component';
import {InfoCircleComponent} from '../../components/info-circle/info-circle.component';
import {PopoverMenuComponent} from '../../../moti/components/popover-menu/popover-menu.component';
import {MotiOptionGroup, MotiOption} from '../../../moti/models/moti-option';

// App Misc
import {AppState} from '../../../app/app.state';
import type { Option } from '../../../shared/models/option';
import { SortTable } from '../../../shared/models/sort-table/types';
import {
  SortTableCellTitleDescription,
  SortTableCellIcon,
  SortTableCellSwatch,
  EMPTY_CELL_DISPLAY,
} from '../../../shared/models/sort-table/cell-classes';
import { ConversionFormula } from '../../../shared/models/set-point';

@Component({
  selector: 'tile-glossary',
  templateUrl: './tile-glossary.component.html',
  styleUrls: ['./tile-glossary.component.scss'],
  imports: [
    SortTableComponent,
    SubnavComponent,
    InfoCircleComponent,
    PopoverMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [IconPickerComponent, ColorPickerComponent, TileEditorComponent]
})

export class TileGlossaryComponent implements OnInit {
  // Expose enums and constants to template
  readonly DeviceType = DeviceType;
  readonly TileFormat = TileFormat;
  readonly formulaTemplates = FORMULA_TEMPLATES;
  readonly FORMULA_MAX_BINDINGS = FORMULA_MAX_BINDINGS;

  formulaTemplateMenuOpen = false;
  readonly formulaTemplateMenuGroup = new MotiOptionGroup<string>(
    FORMULA_TEMPLATES.map(t => ({ id: t.id, name: t.name }))
  );
  readonly overlayPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  }];

  icons: string[];
  colors: readonly ColorOption[];
  conversionFormulas: Option[];
  decimalOptions: number[];
  writabilityOptions: Option[];
  multiplierOptions: Option[];

  currentView: TileFormat = TileFormat.Numeric;
  currentProtocol: DeviceType = DeviceType.BACnet;
  subnavItems: SubnavItem[] = [];
  protocolSubnavItems: SubnavItem[] = [];

  // Table data sources
  writabilityTable$ = new BehaviorSubject<SortTable>(null);
  colorsTable$ = new BehaviorSubject<SortTable>(null);
  decimalTable$ = new BehaviorSubject<SortTable>(null);
  conversionTable$ = new BehaviorSubject<SortTable>(null);
  iconsTable$ = new BehaviorSubject<SortTable>(null);
  multiplierTable$ = new BehaviorSubject<SortTable>(null);
  autoClearTable$ = new BehaviorSubject<SortTable>(null);
  identifiersTable$ = new BehaviorSubject<SortTable>(null);
  alarmConfigTable$ = new BehaviorSubject<SortTable>(null);
  bacnetPointTable$ = new BehaviorSubject<SortTable>(null);
  modbusPointTable$ = new BehaviorSubject<SortTable>(null);
  lorawanPointTable$ = new BehaviorSubject<SortTable>(null);
  formulaConfigTable$ = new BehaviorSubject<SortTable>(null);
  formulaTemplatesTable$ = new BehaviorSubject<SortTable>(null);
  formulaBindingTable$ = new BehaviorSubject<SortTable>(null);
  tagsAndFacetsTable$ = new BehaviorSubject<SortTable>(null);
  profilesTable$ = new BehaviorSubject<SortTable>(null);


  constructor(
    private asideService: AsideService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private iconPickerComponent: IconPickerComponent,
    private colorPickerComponent: ColorPickerComponent,
    private tileEditorComponent: TileEditorComponent,
    private copyToastService: CopyToastService,
    private featureFlagsService: FeatureFlagsService,
    private shadeTools: ShadeToolsService
  ) {
    this.icons = this.iconPickerComponent.options;
    this.colors = this.colorPickerComponent.options;
    this.conversionFormulas = CONVERSION_OPTIONS;
    this.decimalOptions = [...DECIMAL_OPTIONS];
    this.writabilityOptions = this.tileEditorComponent.writabilityOptions;
    this.multiplierOptions = MULTIPLIER_OPTIONS;
  }

  ngOnInit(): void {
    this.updateTitle();
    this.updateAside();
    this.initializeTables();
    this.setupSubnavHandlers();

    this.route.fragment.subscribe(fragment => {
      this.handleFragment(fragment);
    });
  }

  private handleFragment(fragment: string | null): void {
    if (!fragment) return;

    // Check for combined format: view-protocol (e.g., "numeric-bacnet", "numeric-formula")
    if (fragment.includes('-')) {
      const parts = fragment.split('-');
      if (parts.length !== 2) return;

      const [viewPart, protocolPart] = parts;

      if (viewPart === TileFormat.Numeric || viewPart === TileFormat.Toggle) {
        this.switchView(viewPart as TileFormat);
      }

      const protocol = Object.values(DeviceType).find(v => v === protocolPart);
      if (protocol) this.switchProtocol(protocol);
      return;
    }

    // Single anchor — treat as protocol (legacy + formula shorthand)
    const protocol = Object.values(DeviceType).find(v => v === fragment);
    if (protocol) {
      if (protocol === DeviceType.Formula) this.switchView(TileFormat.Numeric);
      this.switchProtocol(protocol);
    } else if (fragment === TileFormat.Numeric || fragment === TileFormat.Toggle) {
      this.switchView(fragment as TileFormat);
    }
  }

  private setupSubnavHandlers(): void {
    this.subnavItems = [
      {
        text: 'Numeric Tiles',
        href: '#' + TileFormat.Numeric,
        onClick: () => this.switchView(TileFormat.Numeric)
      },
      {
        text: 'Toggle Tiles',
        href: '#' + TileFormat.Toggle,
        onClick: () => this.switchView(TileFormat.Toggle)
      }
    ];

    // Protocol subnav for point configuration section
    this.protocolSubnavItems = [
      {
        text: DEVICE_TYPE_DISPLAY[DeviceType.BACnet],
        href: '#bacnet',
        onClick: () => this.switchProtocol(DeviceType.BACnet)
      },
      {
        text: DEVICE_TYPE_DISPLAY[DeviceType.Modbus],
        href: '#modbus',
        onClick: () => this.switchProtocol(DeviceType.Modbus)
      },
      {
        text: DEVICE_TYPE_DISPLAY[DeviceType.LoRaWAN],
        href: '#lorawan',
        onClick: () => this.switchProtocol(DeviceType.LoRaWAN)
      }
    ];

    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_FORMULA_TILE_CREATION)) {
      this.protocolSubnavItems.push({
        text: DEVICE_TYPE_DISPLAY[DeviceType.Formula],
        href: '#formula',
        onClick: () => this.switchProtocol(DeviceType.Formula)
      });
    }
  }

  switchProtocol(protocol: DeviceType): void {
    this.currentProtocol = protocol;
    this.updateUrl();
  }

  switchView(view: TileFormat): void {
    this.currentView = view;
    this.updateViewSpecificTables();
    this.updateUrl();
  }

  private updateUrl(): void {
    const currentUrl = window.location.href.split('#')[0];
    const fragment = `${this.currentView}-${this.currentProtocol.toLowerCase()}`;
    window.history.replaceState(null, '', currentUrl + '#' + fragment);
  }

  private updateViewSpecificTables(): void {
    this.updateUserInputTable();
    this.initializeAlarmConfigTable();
  }

  private initializeProtocolTables(): void {
    this.initializeBACnetPointTable();
    this.initializeModbusPointTable();
    this.initializeLoRaWANPointTable();
    this.initializeFormulaConfigTable();
    this.initializeFormulaTemplatesTable();
    this.initializeFormulaBindingTable();
  }

  getVisibleSections(): string[] {
    const sharedSections = ['writability', 'colors', 'icons'];

    if (this.currentView === TileFormat.Numeric) {
      return [...sharedSections, 'decimal', 'conversion', 'multiplier'];
    }

    return sharedSections;
  }

  isModbusEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MODBUS_BULK_CREATION);
  }

  isLoRaWANEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_LORAWAN_BULK_CREATION);
  }

  get filteredProtocolSubnavItems(): SubnavItem[] {
    return this.protocolSubnavItems.filter(item => {
      if (item.text === DEVICE_TYPE_DISPLAY[DeviceType.Modbus]) return this.isModbusEnabled();
      if (item.text === DEVICE_TYPE_DISPLAY[DeviceType.LoRaWAN]) return this.isLoRaWANEnabled();
      if (item.text === DEVICE_TYPE_DISPLAY[DeviceType.Formula]) return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_FORMULA_TILE_CREATION);
      return true;
    });
  }

  private initializeTables() {
    // Initialize static tables that don't change based on view
    this.initializeStaticTables();

    // Initialize dynamic tables
    this.updateViewSpecificTables();
    this.initializeProtocolTables();
  }

  private initializeStaticTables(): void {
    this.initializeWritabilityTable();
    this.initializeColorsTable();
    this.initializeDecimalTable();
    this.initializeConversionTable();
    this.initializeIconsTable();
    this.initializeMultiplierTable();
    this.initializeAutoClearTable();
    this.initializeTagsAndFacetsTable();
    this.initializeProfilesTable();
  }

  private initializeBACnetPointTable(): void {
    const rows = [
      {
        context: 'device-id',
        cells: [
          new SortTableCellTitleDescription('Motion Device ID', ''),
          new SortTableCellTitleDescription('11005', ''),
          new SortTableCellTitleDescription('Unique identifier for the device', '')
        ]
      },
      {
        context: 'point-id',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.BACNET_INSTANCE_NUMBER, ''),
          new SortTableCellTitleDescription('172', ''),
          new SortTableCellTitleDescription('BACnet object address', '')
        ]
      },
      {
        context: 'point-type',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.BACNET_OBJECT_TYPE, ''),
          new SortTableCellTitleDescription(BacnetPointType.AnalogValue, ''),
          new SortTableCellTitleDescription(`BACnet object type (analogValue, binaryValue, analogInput, binaryInput, etc.)`, '')
        ]
      }
    ];

    this.bacnetPointTable$.next({
      css: {},
      columns: [
        { css: { 'width': '30%' }, name: 'Field' },
        { css: { 'width': '20%' }, name: 'Example Value' },
        { css: { 'width': '50%' }, name: 'Description' }
      ],
      rows: rows,
      pagination: { enabled: false }
    });
  }

  private initializeModbusPointTable(): void {
    const rows = [
      {
        context: 'device-id',
        cells: [
          new SortTableCellTitleDescription('Motion Device ID', ''),
          new SortTableCellTitleDescription('11005', ''),
          new SortTableCellTitleDescription('Unique identifier for the device', '')
        ]
      },
      {
        context: 'point-id',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.MODBUS_ADDRESS, ''),
          new SortTableCellTitleDescription('200', ''),
          new SortTableCellTitleDescription('Modbus register address', '')
        ]
      },
      {
        context: 'data-type',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.MODBUS_DATA_TYPE, ''),
          new SortTableCellTitleDescription(ModbusDataType.Int16, ''),
          new SortTableCellTitleDescription(`Modbus data type: ${ModbusDataType.Bool} (toggle tiles), ${ModbusDataType.Int16}, ${ModbusDataType.Int32}, ${ModbusDataType.Float32}, ${ModbusDataType.Float64} (numeric tiles)`, '')
        ]
      },
      {
        context: 'byte-order',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.MODBUS_BYTE_ORDER, ''),
          new SortTableCellTitleDescription('Aa', ''),
          new SortTableCellTitleDescription('Endianness for multi-byte values: Aa (Big-endian), aA (Little-endian)', '')
        ]
      },
      {
        context: 'writable',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.MODBUS_FUNCTIONS, ''),
          new SortTableCellTitleDescription('true', ''),
          new SortTableCellTitleDescription('Register type: true for output/holding registers, false for input registers', '')
        ]
      }
    ];

    this.modbusPointTable$.next({
      css: {},
      columns: [
        { css: { 'width': '30%' }, name: 'Field' },
        { css: { 'width': '20%' }, name: 'Example Value' },
        { css: { 'width': '50%' }, name: 'Description' }
      ],
      rows: rows,
      pagination: { enabled: false }
    });
  }

  private initializeLoRaWANPointTable(): void {
    const rows = [
      {
        context: 'device-id',
        cells: [
          new SortTableCellTitleDescription('Motion Device ID', ''),
          new SortTableCellTitleDescription('11005', ''),
          new SortTableCellTitleDescription('Unique identifier for the device', '')
        ]
      },
      {
        context: 'point-name',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.LORAWAN_POINT_NAME, ''),
          new SortTableCellTitleDescription('temperature', ''),
          new SortTableCellTitleDescription('Name of the decoded payload field (e.g., temperature, humidity, battery)', '')
        ]
      },
      {
        context: 'point-type',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.LORAWAN_DATA_SOURCE, ''),
          new SortTableCellTitleDescription(LoRaWANPointType.UplinkPayload, ''),
          new SortTableCellTitleDescription(`Data source: ${LoRaWANPointType.UplinkPayload} (Uplink Payload), ${LoRaWANPointType.UplinkMetadata} (Uplink Metadata)`, '')
        ]
      },
      {
        context: 'point-format',
        cells: [
          new SortTableCellTitleDescription(ProtocolPropertyLabel.LORAWAN_DATA_TYPE, ''),
          new SortTableCellTitleDescription(LoRaWANPointFormat.Numeric, ''),
          new SortTableCellTitleDescription(`Data format: ${LoRaWANPointFormat.Numeric} (numeric tiles), ${LoRaWANPointFormat.Boolean} (toggle tiles)`, '')
        ]
      }
    ];

    this.lorawanPointTable$.next({
      css: {},
      columns: [
        { css: { 'width': '30%' }, name: 'Field' },
        { css: { 'width': '20%' }, name: 'Example Value' },
        { css: { 'width': '50%' }, name: 'Description' }
      ],
      rows: rows,
      pagination: { enabled: false }
    });
  }

  private updateUserInputTable(): void {
    const isNumeric = this.currentView === TileFormat.Numeric;

    const baseRows = [
      {
        context: 'tile-name',
        cells: [
          new SortTableCellTitleDescription('Tile Name', ''),
          new SortTableCellTitleDescription(isNumeric ? 'AHU-1 Supply Temp' : 'AHU-1 Fan Status', ''),
          new SortTableCellTitleDescription('Required. Internal identifier for the tile.', ''),
        ]
      },
      {
        context: 'display-name',
        cells: [
          new SortTableCellTitleDescription('Display Name', ''),
          new SortTableCellTitleDescription(isNumeric ? 'Supply Air Temperature' : 'Air Handler Fan', ''),
          new SortTableCellTitleDescription('User-facing label shown on the tile.', ''),
        ]
      },
    ];

    // Add view-specific rows
    if (isNumeric) {
      baseRows.push({
        context: 'units',
        cells: [
          new SortTableCellTitleDescription('Data Display Units', ''),
          new SortTableCellTitleDescription('°F', ''),
          new SortTableCellTitleDescription('Free-text unit label displayed after numeric values (e.g., °F, PSI, kW).', ''),
        ]
      });
      baseRows.push({
        context: 'calibration-offset',
        cells: [
          new SortTableCellTitleDescription('Calibration Offset', ''),
          new SortTableCellTitleDescription('0', ''),
          new SortTableCellTitleDescription('Numeric value added to sensor reading.', ''),
        ]
      });
      baseRows.push({
        context: 'min-writable',
        cells: [
          new SortTableCellTitleDescription('Minimum Writable Value', ''),
          new SortTableCellTitleDescription('0', ''),
          new SortTableCellTitleDescription('Minimum value users can set (write constraint).', ''),
        ]
      });
      baseRows.push({
        context: 'max-writable',
        cells: [
          new SortTableCellTitleDescription('Maximum Writable Value', ''),
          new SortTableCellTitleDescription('100', ''),
          new SortTableCellTitleDescription('Maximum value users can set (write constraint).', ''),
        ]
      });
    } else {
      baseRows.push({
        context: 'true-phrase',
        cells: [
          new SortTableCellTitleDescription('Data Display True Phrase', ''),
          new SortTableCellTitleDescription('On', ''),
          new SortTableCellTitleDescription('Text displayed when toggle tile value is true.', ''),
        ]
      });
      baseRows.push({
        context: 'false-phrase',
        cells: [
          new SortTableCellTitleDescription('Data Display False Phrase', ''),
          new SortTableCellTitleDescription('Off', ''),
          new SortTableCellTitleDescription('Text displayed when toggle tile value is false.', ''),
        ]
      });
    }

    // Add linking fields
    baseRows.push(
      {
        context: 'linked-app-ids',
        cells: [
          new SortTableCellTitleDescription('Linked App IDs', ''),
          new SortTableCellTitleDescription('42216,42217', ''),
          new SortTableCellTitleDescription('Comma-separated app IDs, no spaces.', ''),
        ]
      },
      {
        context: 'linked-super-tile-ids',
        cells: [
          new SortTableCellTitleDescription('Linked Super Tile IDs', ''),
          new SortTableCellTitleDescription('1993234,1993235', ''),
          new SortTableCellTitleDescription('Comma-separated tile IDs to add to existing super tiles.', ''),
        ]
      }
    );

    this.identifiersTable$.next({
      css: {},
      columns: [
        {
          css: { 'width': '30%' },
          name: 'Field Name',
        },
        {
          css: { 'width': '40%' },
          name: 'Example Values',
        },
        {
          css: { 'width': '30%' },
          name: 'Description',
        },
      ],
      rows: baseRows,
      pagination: { enabled: false }
    });
  }

  private initializeAlarmConfigTable(): void {
    let rows = [];

    if (this.currentView === TileFormat.Numeric) {
      rows.push(
        {
          context: 'enabled',
          cells: [
            new SortTableCellTitleDescription('[Type]: Enabled', ''),
            new SortTableCellTitleDescription('true, false', ''),
            new SortTableCellTitleDescription('Enable this alarm level.', ''),
          ]
        },
        {
          context: 'operator',
          cells: [
            new SortTableCellTitleDescription('[Type]: Operator', ''),
            new SortTableCellTitleDescription('>, <, >=, <=', ''),
            new SortTableCellTitleDescription('Comparison operator for threshold.', ''),
          ]
        },
        {
          context: 'value',
          cells: [
            new SortTableCellTitleDescription('[Type]: Value', ''),
            new SortTableCellTitleDescription('80, 32, 100.5, -10', ''),
            new SortTableCellTitleDescription('Numeric threshold value', ''),
          ]
        },
        {
          context: 'delay',
          cells: [
            new SortTableCellTitleDescription('[Type]: Delay', ''),
            new SortTableCellTitleDescription('60', ''),
            new SortTableCellTitleDescription('Seconds before triggering alarm.', ''),
          ]
        },
        {
          context: 'message',
          cells: [
            new SortTableCellTitleDescription('[Type]: Message', ''),
            new SortTableCellTitleDescription('High temperature warning!', ''),
            new SortTableCellTitleDescription('Custom alarm message', ''),
          ]
        },
        {
          context: 'icon',
          cells: [
            new SortTableCellTitleDescription('[Type]: Icon', ''),
            new SortTableCellTitleDescription('fire, snowflake, exclamation', ''),
            new SortTableCellTitleDescription('Visual indicator icon', ''),
          ]
        }
      );
    } else {
      rows.push(
        {
          context: 'enabled',
          cells: [
            new SortTableCellTitleDescription('Critical: Enabled', ''),
            new SortTableCellTitleDescription('true, false', ''),
            new SortTableCellTitleDescription('Enable critical alarm.', ''),
          ]
        },
        {
          context: 'value',
          cells: [
            new SortTableCellTitleDescription('Critical: Value', ''),
            new SortTableCellTitleDescription('true, false', ''),
            new SortTableCellTitleDescription('Trigger alarm when tile equals this state.', ''),
          ]
        },
        {
          context: 'delay',
          cells: [
            new SortTableCellTitleDescription('Critical: Delay', ''),
            new SortTableCellTitleDescription('0', ''),
            new SortTableCellTitleDescription('Seconds before triggering alarm.', ''),
          ]
        },
        {
          context: 'message',
          cells: [
            new SortTableCellTitleDescription('Critical: Message', ''),
            new SortTableCellTitleDescription('Equipment failure detected!', ''),
            new SortTableCellTitleDescription('Custom alarm message', ''),
          ]
        },
        {
          context: 'icon',
          cells: [
            new SortTableCellTitleDescription('Critical: Icon', ''),
            new SortTableCellTitleDescription('exclamation-circle, alert-triangle', ''),
            new SortTableCellTitleDescription('Visual indicator icon', ''),
          ]
        }
      );
    }

    this.alarmConfigTable$.next({
      css: {},
      columns: [
        {
          css: { 'width': '30%' },
          name: 'Field Name',
        },
        {
          css: { 'width': '40%' },
          name: 'Example Values',
        },
        {
          css: { 'width': '30%' },
          name: 'Description',
        },
      ],
      rows: rows,
      pagination: { enabled: false }
    });
  }

  private initializeWritabilityTable(): void {
    this.writabilityTable$.next({
      css: {},
      columns: [
        { css: { 'width': '50%' }, name: 'Value' },
        { css: { 'width': '50%' }, name: 'Result' },
      ],
      rows: [
        {
          context: 'false',
          cells: [
            new SortTableCellTitleDescription('false', '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription('Read-Only', ''),
          ]
        },
        {
          context: 'true',
          cells: [
            new SortTableCellTitleDescription('true', '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription('Read+Write', ''),
          ]
        }
      ],
      pagination: { enabled: false }
    });
  }

  private initializeColorsTable(): void {
    this.colorsTable$.next({
      css: {},
      columns: [
        { css: { 'width': '60%' }, name: 'Value' },
        { css: { 'width': '40%' }, name: 'Result' },
      ],
      rows: this.colors.map(color => ({
        context: color,
        cells: [
          new SortTableCellTitleDescription(color || EMPTY_CELL_DISPLAY, '').makeCopyable(this.copyToastService),
          new SortTableCellSwatch(color as any),
        ]
      })),
      pagination: this.colors.length > 12 ? { enabled: true, rowsPerPage: 12, startOnPage: 1, maxPageOptions: 12 } : { enabled: false }
    });
  }

  private initializeDecimalTable(): void {
    this.decimalTable$.next({
      css: {},
      columns: [
        { css: { 'width': '50%' }, name: 'Value' },
        { css: { 'width': '50%' }, name: 'Result' },
      ],
      rows: this.decimalOptions.map(decimal => ({
        context: decimal.toString(),
        cells: [
          new SortTableCellTitleDescription(decimal.toString(), '').makeCopyable(this.copyToastService),
          new SortTableCellTitleDescription(`${this.getDecimalExample(decimal)}`, ''),
        ]
      })),
      pagination: { enabled: false }
    });
  }

  private initializeConversionTable(): void {
    this.conversionTable$.next({
      css: {},
      columns: [
        { css: { 'width': '50%' }, name: 'Value' },
        { css: { 'width': '50%' }, name: 'Description' },
      ],
      rows: this.conversionFormulas.map(formula => {
        const example = this.getConversionExample(formula.id);
        const isNone = formula.id === ConversionFormula.None;
        const displayText = isNone ? 'Clears an existing conversion formula. Enter "None"; a blank cell leaves the current conversion unchanged' : (example ? `${formula.name} (${example})` : formula.name);
        return {
          context: formula.id,
          cells: [
            new SortTableCellTitleDescription(isNone ? 'None' : formula.id, '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription(displayText, ''),
          ]
        };
      }),
      pagination: { enabled: false }
    });
  }

  private initializeIconsTable(): void {
    this.iconsTable$.next({
      css: {},
      columns: [
        {
          css: { 'width': '70%' },
          name: 'Value',
          sortingFn: SortTableCellTitleDescription.sortingFnTitle,
          sortDefault: 'up',
        },
        { css: { 'width': '30%' }, name: 'Result' },
      ],
      rows: this.icons.map(icon => ({
        context: icon,
        cells: [
          new SortTableCellTitleDescription(icon, '').makeCopyable(this.copyToastService),
          new SortTableCellIcon(icon),
        ]
      })),
      pagination: { enabled: true, rowsPerPage: 20, startOnPage: 1, maxPageOptions: 12 }
    });
  }

  private initializeMultiplierTable(): void {
    this.multiplierTable$.next({
      css: {},
      columns: [
        { css: { 'width': '50%' }, name: 'Value' },
        { css: { 'width': '50%' }, name: 'Result' },
      ],
      rows: this.multiplierOptions.map(option => ({
        context: option.id,
        cells: [
          new SortTableCellTitleDescription(option.id, '').makeCopyable(this.copyToastService),
          new SortTableCellTitleDescription(option.name, ''),
        ]
      })),
      pagination: { enabled: false }
    });
  }

  private initializeAutoClearTable(): void {
    this.autoClearTable$.next({
      css: {},
      columns: [
        { css: { 'width': '50%' }, name: 'Value' },
        { css: { 'width': '50%' }, name: 'Description' },
      ],
      rows: [
        {
          context: 'empty',
          cells: [
            new SortTableCellTitleDescription('(leave empty)', '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription('New tiles: never (Alarms must be manually dismissed). Existing tiles: leaves the current setting unchanged', ''),
          ]
        },
        {
          context: 'none',
          cells: [
            new SortTableCellTitleDescription('none', '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription('Never (Alarms must be manually dismissed). Use this to turn auto-clear off on existing tiles', ''),
          ]
        },
        {
          context: 'acked',
          cells: [
            new SortTableCellTitleDescription('acked', '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription('Only if acknowledged (Auto-dismiss only after user acknowledgment)', ''),
          ]
        },
        {
          context: 'all',
          cells: [
            new SortTableCellTitleDescription('all', '').makeCopyable(this.copyToastService),
            new SortTableCellTitleDescription('Always (Auto-dismiss as soon as condition resolves)', ''),
          ]
        }
      ],
      pagination: { enabled: false }
    });
  }

  private initializeTagsAndFacetsTable(): void {
    this.tagsAndFacetsTable$.next({
      css: {},
      columns: [
        { css: { 'width': '20%' }, name: 'Column' },
        { css: { 'width': '30%' }, name: 'Format' },
        { css: { 'width': '25%' }, name: 'Example' },
        { css: { 'width': '25%' }, name: 'Notes' },
      ],
      rows: [
        {
          context: 'profile',
          cells: [
            new SortTableCellTitleDescription(CsvFieldId.PROFILE, ''),
            new SortTableCellTitleDescription(Object.values(TAG_PROFILES).map(p => p.Label).join(' | '), ''),
            new SortTableCellTitleDescription(Object.values(TAG_PROFILES)[0]?.Label ?? '', ''),
            new SortTableCellTitleDescription('Optional. When set, facets are resolved using the profile\'s fixed facet IDs. If omitted, the profile is detected from the facet labels. Exported automatically from tile facets.', ''),
          ]
        },
        {
          context: 'tags',
          cells: [
            new SortTableCellTitleDescription('Tags', ''),
            new SortTableCellTitleDescription('Label1|Label2|…', ''),
            new SortTableCellTitleDescription('Critical|Legacy', ''),
            new SortTableCellTitleDescription('Optional. Pipe-separated tag labels. Each label is created if it does not exist.', ''),
          ]
        },
        {
          context: 'facets',
          cells: [
            new SortTableCellTitleDescription('Facets', ''),
            new SortTableCellTitleDescription('KEY=Value|KEY=Value|…', ''),
            new SortTableCellTitleDescription('BUILDING=Building|LOCATION=Floor 1 – North', ''),
            new SortTableCellTitleDescription('Optional. KEY must match an existing facet on the account. Value is required.', ''),
          ]
        },
      ],
      pagination: { enabled: false }
    });
  }

  private initializeProfilesTable(): void {
    this.profilesTable$.next({
      css: {},
      columns: [
        { css: { 'width': '25%' }, name: 'Profile' },
        { css: { 'width': '75%' }, name: 'Facet Labels' },
      ],
      rows: Object.entries(TAG_PROFILES).map(([, profile]) => ({
        context: profile.Label,
        cells: [
          new SortTableCellTitleDescription(profile.Label, '').makeCopyable(this.copyToastService),
          new SortTableCellTitleDescription(profile.Facets.map(f => f.Label).join(', '), ''),
        ]
      })),
      pagination: { enabled: false }
    });
  }

  private getDecimalExample(decimal: number): string {
    const value = 123.456789;
    return value.toFixed(decimal);
  }

  private getConversionExample(id: string): string {
    const examples = {
      [ConversionFormula.FahrenheitToCelsius]: '32°F → 0°C',
      [ConversionFormula.CelsiusToFahrenheit]: '0°C → 32°F',
      [ConversionFormula.KiloPascalToPSI]: '100 kPa → 14.5 psi',
      [ConversionFormula.PSIToKiloPascal]: '14.5 psi → 100 kPa',
      [ConversionFormula.LitersPerSecondToCFM]: '10 l/s → 21.2 CFM',
      [ConversionFormula.CFMToLitersPerSecond]: '21.2 CFM → 10 l/s',
    };
    return examples[id] || '';
  }

  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      null,
      'Tile Glossary',
      [
        {
          text: 'More Info',
          icon: 'info-circle',
          callback: () => {
            this.shadeTools.infoDialog(
              'This glossary shows bulk tile creation configuration terms mapped to their possible values for a given column. Click on values to copy them to clipboard.',
              'Tile Glossary'
            );
            return {};
          }
        }
      ]
    ));
  }

  private updateAside() {
    this.asideService.replaceContextSections([]);
  }

  private initializeFormulaConfigTable(): void {
    this.formulaConfigTable$.next({
      css: {},
      columns: [
        { css: { 'width': '30%' }, name: 'Field' },
        { css: { 'width': '20%' }, name: 'Example Value' },
        { css: { 'width': '50%' }, name: 'Description' },
      ],
      rows: [
        {
          context: 'formula-type',
          cells: [
            new SortTableCellTitleDescription(CsvFieldId.FORMULA_TYPE, ''),
            new SortTableCellTitleDescription(FormulaType.Template, ''),
            new SortTableCellTitleDescription(`"${FormulaType.Template}" for pre-built formulas, "${FormulaType.Custom}" for custom expressions`, ''),
          ]
        },
        {
          context: 'formula-value',
          cells: [
            new SortTableCellTitleDescription(CsvFieldId.FORMULA_VALUE, ''),
            new SortTableCellTitleDescription('dew-point-f', ''),
            new SortTableCellTitleDescription('Template ID (see table below) or custom expression', ''),
          ]
        }
      ],
      pagination: { enabled: false }
    });
  }

  private initializeFormulaTemplatesTable(): void {
    this.formulaTemplatesTable$.next({
      css: {},
      columns: [
        { css: { 'width': '30%' }, name: 'Template ID' },
        { css: { 'width': '25%' }, name: 'Name' },
        { css: { 'width': '45%' }, name: 'Variables' },
      ],
      rows: FORMULA_TEMPLATES.map(template => ({
        context: template.id,
        cells: [
          new SortTableCellTitleDescription(template.id, '').makeCopyable(this.copyToastService),
          new SortTableCellTitleDescription(template.name, ''),
          new SortTableCellTitleDescription(
            template.variables.map(v => `${v.key} (${v.label})`).join(', '),
            ''
          ),
        ]
      })),
      pagination: { enabled: false }
    });
  }

  private initializeFormulaBindingTable(): void {
    this.formulaBindingTable$.next({
      css: {},
      columns: [
        { css: { 'width': '35%' }, name: 'Field' },
        { css: { 'width': '20%' }, name: 'Example Value' },
        { css: { 'width': '45%' }, name: 'Description' },
      ],
      rows: [
        {
          context: 'variable',
          cells: [
            new SortTableCellTitleDescription(`Binding N: Variable`, ''),
            new SortTableCellTitleDescription('tc', ''),
            new SortTableCellTitleDescription('Variable key matching a template variable', ''),
          ]
        },
        {
          context: 'device-id',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.MOTION_DEVICE_ID}`, ''),
            new SortTableCellTitleDescription('11005', ''),
            new SortTableCellTitleDescription('ID of the LoRaWAN device providing this binding\'s data', ''),
          ]
        },
        {
          context: 'point-name',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.LORAWAN_POINT_NAME}`, ''),
            new SortTableCellTitleDescription('temperature', ''),
            new SortTableCellTitleDescription('Name of the decoded payload field', ''),
          ]
        },
        {
          context: 'data-source',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.LORAWAN_DATA_SOURCE}`, ''),
            new SortTableCellTitleDescription('UP', ''),
            new SortTableCellTitleDescription('UP (Uplink Payload) or UM (Uplink Metadata)', ''),
          ]
        },
        {
          context: 'data-type',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.LORAWAN_DATA_TYPE}`, ''),
            new SortTableCellTitleDescription('num', ''),
            new SortTableCellTitleDescription('num (numeric) or bool (boolean)', ''),
          ]
        },
        {
          context: 'multiplier',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.MULTIPLIER}`, ''),
            new SortTableCellTitleDescription('1', ''),
            new SortTableCellTitleDescription('Multiplier applied to this binding\'s value', ''),
          ]
        },
        {
          context: 'conversion',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA}`, ''),
            new SortTableCellTitleDescription('', ''),
            new SortTableCellTitleDescription('Unit conversion applied to this binding\'s value', ''),
          ]
        },
        {
          context: 'offset',
          cells: [
            new SortTableCellTitleDescription(`Binding N: ${CsvFieldId.CALIBRATION_OFFSET}`, ''),
            new SortTableCellTitleDescription('0', ''),
            new SortTableCellTitleDescription('Calibration offset added to this binding\'s value', ''),
          ]
        },
      ],
      pagination: { enabled: false }
    });
  }

  onFormulaTemplateSelected(option: MotiOption<string>): void {
    this.formulaTemplateMenuOpen = false;
    this.downloadFormulaTemplateExample(option.id);
  }

  onFormulaTemplateMenuClosed(): void {
    this.formulaTemplateMenuOpen = false;
  }

  downloadFormulaTemplateExample(templateId: string | null): void {
    CSVConfigExporter.downloadFormulaConfigTemplate(templateId).subscribe({
      error: (error) => {
        console.error('Failed to download formula template:', error);
        this.shadeTools.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  downloadNumericExample(protocol: DeviceType): void {
    // Use the CSV utility to generate proper examples
    const examples = CSVTemplateGenerator.generateNumericTemplate({ protocol });
    CSVTemplateGenerator.downloadCSV(examples, `${protocol}-numeric-tiles-example.csv`).subscribe({
      error: (error) => {
        console.error('Failed to download example:', error);
        this.shadeTools.errorDialog(`Failed to download example: ${error.message || 'Unknown error'}`);
      }
    });
  }

  downloadToggleExample(protocol: DeviceType): void {
    // Use the CSV utility to generate proper examples
    const examples = CSVTemplateGenerator.generateToggleTemplate({ protocol });
    CSVTemplateGenerator.downloadCSV(examples, `${protocol}-toggle-tiles-example.csv`).subscribe({
      error: (error) => {
        console.error('Failed to download example:', error);
        this.shadeTools.errorDialog(`Failed to download example: ${error.message || 'Unknown error'}`);
      }
    });
  }
}
